import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useCanvasEngine } from "../hooks/useCanvasEngine";
import { useDrawing } from "../hooks/useDrawing";
import { useFog } from "../hooks/useFog";
import { useDynamicFog } from "../hooks/useDynamicFog";
import type { UseDynamicFogReturn } from "../hooks/useDynamicFog";
import { useWalls } from "../hooks/useWalls";
import { useLocationLinks } from "../hooks/useLocationLinks";
import { useMapObjects } from "../hooks/useMapObjects";
import type { UseMapObjectsReturn } from "../hooks/useMapObjects";
import { useSessionContext } from "./SessionContext";
import { useUndoContext } from "./UndoContext";
import { createDrawingEraseCommand } from "../lib/undo-commands";
import type { Wall, FogReveal, SerializedToken, GlobalIllumination, PageState } from "@roll1/shared";
type CanvasEngine = ReturnType<typeof useCanvasEngine>;
type UseDrawingReturn = ReturnType<typeof useDrawing>;
type UseFogReturn = ReturnType<typeof useFog>;
type UseWallsReturn = ReturnType<typeof useWalls>;
type UseLocationLinksReturn = ReturnType<typeof useLocationLinks>;

export interface WallChainPreview {
  points: Array<{ x: number; y: number }>;
  previewPoint: { x: number; y: number } | null;
  hoveredWallId: string | null;
  /** Snap indicator: when cursor is near an existing wall endpoint */
  snapPoint: { x: number; y: number } | null;
}

export interface CanvasContextValue {
  engine: CanvasEngine;
  drawing: UseDrawingReturn;
  fog: UseFogReturn;
  dynamicFog: UseDynamicFogReturn;
  dynamicFogTokensRef: React.MutableRefObject<SerializedToken[]>;
  wallsHook: UseWallsReturn;
  walls: React.MutableRefObject<Wall[]>;
  wallChainPreview: React.MutableRefObject<WallChainPreview>;
  locationLinks: UseLocationLinksReturn;
  mapObjects: UseMapObjectsReturn;
  globalIlluminationRef: React.MutableRefObject<GlobalIllumination>;
  controlledTokenIdsRef: React.MutableRefObject<Set<string>>;
  snapToGrid: boolean;
  setSnapToGrid: React.Dispatch<React.SetStateAction<boolean>>;
  drawColor: string;
  setDrawColor: (color: string) => void;
  drawWidth: number;
  setDrawWidth: (width: number) => void;
}

const CanvasContext = createContext<CanvasContextValue | null>(null);

export function CanvasProvider({ children }: { children: ReactNode }) {
  const { mp, isGM, sessionSettings, pages, outgoingPageId } =
    useSessionContext();
  const undoStack = useUndoContext();
  const engine = useCanvasEngine();

  const eraseBatchRef = useRef<string[]>([]);
  const eraseTimerRef = useRef<number>(0);

  const drawing = useDrawing(engine, {
    onRulerShow: (points, totalDistance, persistent) => {
      if (mp.session) {
        mp.send({
          type: "ruler:show",
          points,
          totalDistance,
          persistent,
        } as any);
      }
    },
    onRulerHide: () => {
      if (mp.session) {
        mp.send({ type: "ruler:hide" } as any);
      }
    },
    onEraseStroke: (strokeIndex, stroke) => {
      undoStack.push(
        createDrawingEraseCommand({
          stroke,
          addStroke: (restoredStroke) => {
            drawing.strokes.current.push(restoredStroke);
            drawing.invalidateCache();
            engine.markDirty("drawing");
          },
          removeStrokeById: (strokeId) => {
            drawing.strokes.current = drawing.strokes.current.filter(
              (item) => item.id !== strokeId,
            );
            drawing.invalidateCache();
            engine.markDirty("drawing");
          },
          sendStroke: (restoredStroke) => {
            if (mp.session) {
              mp.send({
                type: "drawing:stroke",
                stroke: restoredStroke,
                pageId: outgoingPageId,
              } as any);
            }
          },
          sendErase: (strokeId) => {
            if (mp.session) {
              mp.send({
                type: "drawing:erase",
                strokeId,
                pageId: outgoingPageId,
              } as any);
            }
          },
        }),
      );
      if (mp.session) {
        eraseBatchRef.current.push(stroke.id);
        if (eraseTimerRef.current) clearTimeout(eraseTimerRef.current);
        eraseTimerRef.current = window.setTimeout(() => {
          const ids = eraseBatchRef.current.splice(0);
          if (ids.length > 0 && mp.session) {
            mp.send({
              type: "drawing:erase-batch",
              strokeIds: ids,
              pageId: outgoingPageId,
            } as any);
          }
        }, 80);
      }
    },
    measurementMode: sessionSettings.measurementMode,
    gridSizeFt: sessionSettings.gridSizeFt,
  });

  const fog = useFog(engine, isGM);

  const wallsHookReturn = useWalls(
    engine,
    (msg) => { if (mp.session) mp.send(msg); },
    isGM,
  );

  // Tokens ref for dynamic fog — uses SerializedToken[] from page data
  const dynamicFogTokensRef = useRef<SerializedToken[]>([]);
  const controlledTokenIdsRef = useRef(new Set<string>());

  // Lighting refs for dynamic fog
  const globalIlluminationRef = useRef<GlobalIllumination>({ level: 'bright' });
  const pageDataRef = useRef<PageState | null>(null);
  const gridSizeFtRef = useRef<number>(sessionSettings.gridSizeFt ?? 5);
  // Keep the ref in sync with the live setting — it was frozen at the
  // first-render value, so dynamic fog and vision computed all radii against
  // the default 5 ft even after the GM changed the grid scale.
  useEffect(() => {
    gridSizeFtRef.current = sessionSettings.gridSizeFt ?? 5;
  }, [sessionSettings.gridSizeFt]);

  // Map objects hook (before dynamic fog, so ref is available)
  const mapObjects = useMapObjects({ engine, isGM, gridSizeFtRef });

  // Dynamic fog hook
  const dynamicFog = useDynamicFog(
    engine,
    wallsHookReturn.walls,
    wallsHookReturn.wallsVersion,
    dynamicFogTokensRef,
    fog.revealed,
    isGM,
    controlledTokenIdsRef,
    (explored: FogReveal[]) => {
      if (mp.session) {
        mp.send({
          type: 'fog:explored-update',
          explored,
          pageId: outgoingPageId,
        } as any);
      }
    },
    globalIlluminationRef,
    pageDataRef,
    gridSizeFtRef,
    mapObjects.objects,
  );

  // Wire dynamic fog renderer into useFog
  useEffect(() => {
    fog.setDynamicFogRenderer(dynamicFog.renderDynamicFog);
    return () => fog.setDynamicFogRenderer(null);
  }, [fog, dynamicFog.renderDynamicFog]);

  // Compute controlled token IDs: tokens bound to sheets owned by current participant
  useEffect(() => {
    const myId = mp.participant?.id;
    const sheets = mp.serverState?.characterSheets;
    if (!myId || !sheets) {
      controlledTokenIdsRef.current = new Set<string>();
      return;
    }
    const myName = mp.participant?.name;
    const myUserId = mp.participant?.userId;
    const mySheetIds = new Set(
      sheets.filter(s =>
        (myUserId && s.ownerId ? s.ownerId === myUserId : false) || s.ownerName === myName
      ).map(s => s.id)
    );
    const pid = pages.viewingPageId;
    if (!pid) return;
    const pd = pages.getPageData(pid);
    if (!pd) return;
    const ids = new Set<string>();
    for (const token of pd.tokens) {
      if (token.characterSheetId && mySheetIds.has(token.characterSheetId)) {
        ids.add(token.id);
      }
    }
    // Include GM-assigned tokens
    const assigned = mp.participant?.assignedTokenIds ?? [];
    for (const tid of assigned) ids.add(tid);
    controlledTokenIdsRef.current = ids;
    if (import.meta.env.DEV) {
      console.warn('[controlled-tokens]', {
        myId, myName, myUserId,
        sheetsCount: sheets.length,
        mySheetIds: [...mySheetIds],
        pageTokens: pd.tokens.map(t => ({ id: t.id.slice(0, 8), name: t.name, sheetId: t.characterSheetId?.slice(0, 8) })),
        assigned,
        controlledIds: [...ids],
        sheetsDebug: sheets.map(s => ({ id: s.id.slice(0, 8), name: s.name, ownerId: s.ownerId?.slice(0, 8), ownerName: s.ownerName })),
      });
    }
    // Trigger re-render so token visibility filter picks up the new set
    dynamicFog.invalidateVisibility();
  }, [mp.participant?.id, mp.participant?.name, mp.participant?.assignedTokenIds, mp.serverState?.characterSheets, pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync tokens and explored from page data for dynamic fog
  useEffect(() => {
    const pid = pages.viewingPageId;
    if (!pid) return;
    const pd = pages.getPageData(pid);
    if (pd) {
      dynamicFogTokensRef.current = pd.tokens ?? [];
      dynamicFog.setExplored([...(pd.fog.explored ?? [])]);
      // BI-001: sync fog mode
      fog.fogModeRef.current = pd.fog.mode ?? 'manual';
      // Dynamic mode requires fog enabled
      if (fog.fogModeRef.current === 'dynamic' && !fog.enabled.current) {
        fog.enabled.current = true;
        engine.markDirty('fog');
      }
      // Sync lighting refs
      pageDataRef.current = pd;
      globalIlluminationRef.current = pd.globalIllumination ?? { level: 'bright' };
    }
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // BI-002: invalidate dynamic fog caches on page switch
  useEffect(() => {
    dynamicFog.invalidateAll();
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const locationLinks = useLocationLinks({
    engine,
    isGM,
    pageIds: pages.pages.map((p) => p.id),
  });

  const wallChainPreview = useRef<WallChainPreview>({ points: [], previewPoint: null, hoveredWallId: null, snapPoint: null });

  const [snapToGrid, setSnapToGrid] = useState(false);
  const [drawColor, setDrawColor] = useState("#ff0000");
  const [drawWidth, setDrawWidth] = useState(3);

  // Sync draw settings to refs
  useEffect(() => {
    drawing.color.current = drawColor;
  }, [drawColor, drawing]);
  useEffect(() => {
    drawing.lineWidth.current = drawWidth;
  }, [drawWidth, drawing]);

  // Sync gridSize/mapScale/bgColor from active page to engine refs (only on page switch)
  useEffect(() => {
    const pid = pages.viewingPageId;
    if (!pid) return;
    const pd = pages.getPageData(pid);
    if (pd) {
      engine.gridSizeRef.current = pd.gridSize ?? 70;
      engine.mapScaleRef.current = (pd as unknown as Record<string, unknown>).mapScale as number ?? 1.0;
      engine.bgColorRef.current = ((pd as unknown as Record<string, unknown>).bgColor as string) || null;
      engine.markAllDirty();
    }
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // V004: Sync gridStyle/gridOpacity from SessionSettings to engine refs
  useEffect(() => {
    if (engine.gridStyleRef) engine.gridStyleRef.current = sessionSettings.gridStyle ?? 'lines';
    if (engine.gridOpacityRef) engine.gridOpacityRef.current = sessionSettings.gridOpacity ?? 100;
    engine.markDirty('grid');
  }, [sessionSettings.gridStyle, sessionSettings.gridOpacity, engine]);

  const value = useMemo(
    () => ({
      engine,
      drawing,
      fog,
      dynamicFog,
      dynamicFogTokensRef,
      wallsHook: wallsHookReturn,
      walls: wallsHookReturn.walls,
      wallChainPreview,
      globalIlluminationRef,
      controlledTokenIdsRef,
      locationLinks,
      mapObjects,
      snapToGrid,
      setSnapToGrid,
      drawColor,
      setDrawColor,
      drawWidth,
      setDrawWidth,
    }),
    [
      engine,
      drawing,
      fog,
      dynamicFog,
      wallsHookReturn,
      locationLinks,
      mapObjects,
      snapToGrid,
      setSnapToGrid,
      drawColor,
      setDrawColor,
      drawWidth,
      setDrawWidth,
    ],
  );

  return (
    <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>
  );
}

export function useCanvasContext() {
  const ctx = useContext(CanvasContext);
  if (!ctx)
    throw new Error("useCanvasContext must be used within CanvasProvider");
  return ctx;
}
